
import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = () => {
    if (!formData.name.trim()) {
      return 'Name is required';
    }
    if (!formData.email.trim()) {
      return 'Email is required';
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return 'Please enter a valid email address';
    }
    if (!formData.subject.trim()) {
      return 'Subject is required';
    }
    if (!formData.message.trim()) {
      return 'Message is required';
    }
    if (formData.message.length > 500) {
      return 'Message must be 500 characters or less';
    }
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4paqkniu80n4l8qnob0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 bg-amber-50/30">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex gap-16">
          <div className="w-3/5">
            <h2 className="text-5xl font-extrabold text-black mb-4">Get In Touch</h2>
            <p className="text-lg text-gray-600 mb-12">Have questions? We&apos;d love to hear from you</p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:border-cyan-400 transition-colors"
              />

              <input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:border-cyan-400 transition-colors"
              />

              <input
                type="text"
                name="subject"
                placeholder="Subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
                className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:border-cyan-400 transition-colors"
              />

              <textarea
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, message: e.target.value });
                  }
                }}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-4 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:border-cyan-400 transition-colors resize-none"
              ></textarea>
              <p className="text-sm text-gray-500 -mt-2">{formData.message.length}/500 characters</p>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  Sorry, there was an error sending your message. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-4 bg-gradient-to-r from-cyan-400 to-blue-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          <div className="w-2/5 pt-10">
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <div className="space-y-10">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 whitespace-nowrap">OFFICE ADDRESS</p>
                  <p className="text-base text-black leading-relaxed">
                    Rua CUSTODIO FREIRE 166<br />
                    RIO BRANCO, 69900-553<br />
                    Brazil
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 whitespace-nowrap">CONTACT INFO</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <i className="ri-phone-line text-lg text-cyan-400"></i>
                      <span className="text-base text-black whitespace-nowrap">+1 785-599-7188</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-mail-line text-lg text-cyan-400"></i>
                      <span className="text-base text-black break-all">support@frigoaveriobrancoltda.com</span>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 whitespace-nowrap">SUPPORT HOURS</p>
                  <p className="text-base text-black whitespace-nowrap">24/7 Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
